import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Container,
  Typography,
  Box,
  Button,
  IconButton,
  Slide,
  useScrollTrigger,
  useTheme,
  Stack,
  Divider,
} from '@mui/material';
import { Login, PersonAdd, Menu as MenuIcon, Close } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import LanguageSwitcher from './LanguageSwitcher';
import { t } from '@/utils/i18n-enhanced';

interface Props {
  window?: () => Window;
  children: React.ReactElement;
}

function HideOnScroll(props: Props) {
  const { children, window } = props;
  const trigger = useScrollTrigger({
    target: window ? window() : undefined,
  });

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

const PublicHeader: React.FC<{
  onFeaturesClick?: () => void;
  onHowItWorksClick?: () => void;
}> = ({ onFeaturesClick, onHowItWorksClick }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleMobileMenuClick = (action?: () => void) => {
    action?.();
    setMobileMenuOpen(false);
  };

  return (
    <>
      <HideOnScroll>
        <AppBar 
          position="fixed" 
          sx={{ 
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            color: 'text.primary', 
            boxShadow: '0 2px 20px rgba(0,0,0,0.08)',
            borderBottom: '1px solid',
            borderColor: 'divider',
            zIndex: theme.zIndex.appBar + 1
          }}
        >
          <Container maxWidth="xl">
            <Toolbar sx={{ py: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                <Typography 
                  variant="h5" 
                  component="div" 
                  onClick={() => navigate('/')}
                  sx={{ 
                    fontWeight: 800,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    mr: 4,
                    cursor: 'pointer'
                  }}
                >
                  souk.immo
                </Typography>
                <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 3, alignItems: 'center' }}>
                  {onFeaturesClick && (
                    <Button color="inherit" onClick={onFeaturesClick} sx={{ fontWeight: 500 }}>
                      {t('navFeatures')}
                    </Button>
                  )}
                  {onHowItWorksClick && (
                    <Button color="inherit" onClick={onHowItWorksClick} sx={{ fontWeight: 500 }}>
                      {t('navHowItWorks')}
                    </Button>
                  )}
                  <Button color="inherit" onClick={() => navigate('/properties')} sx={{ fontWeight: 500 }}>
                    {t('nav_properties')}
                  </Button>
                  <Button color="inherit" onClick={() => navigate('/chantiers')} sx={{ fontWeight: 500 }}>
                    {t('nav_developments')}
                  </Button>
                  <Button color="inherit" onClick={() => navigate('/crm')} sx={{ fontWeight: 500 }}>
                    {t('nav_for_professionals')}
                  </Button>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <LanguageSwitcher />
                <Button
                  variant="text"
                  startIcon={<Login />}
                  onClick={() => navigate('/login')}
                  sx={{ display: { xs: 'none', sm: 'flex' }, fontWeight: 500 }}
                >
                  {t('navLogin')}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<PersonAdd />}
                  onClick={() => navigate('/login')}
                  sx={{ 
                    display: { xs: 'none', sm: 'flex' },
                    fontWeight: 600,
                    px: 3,
                    boxShadow: 2,
                  }}
                >
                  {t('navSignUp')}
                </Button>
                <IconButton
                  sx={{ display: { xs: 'flex', md: 'none' } }}
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                >
                  {mobileMenuOpen ? <Close /> : <MenuIcon />}
                </IconButton>
              </Box>
            </Toolbar>
          </Container>
        </AppBar>
      </HideOnScroll>

      {mobileMenuOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 64,
            left: 0,
            right: 0,
            bgcolor: 'background.paper',
            zIndex: theme.zIndex.appBar,
            boxShadow: 4,
            py: 2,
            display: { xs: 'block', md: 'none' },
          }}
        >
          <Container>
            <Stack spacing={1}>
              {onFeaturesClick && (
                <Button fullWidth onClick={() => handleMobileMenuClick(onFeaturesClick)}>
                  {t('navFeatures')}
                </Button>
              )}
              {onHowItWorksClick && (
                <Button fullWidth onClick={() => handleMobileMenuClick(onHowItWorksClick)}>
                  {t('navHowItWorks')}
                </Button>
              )}
              <Button fullWidth onClick={() => { navigate('/properties'); setMobileMenuOpen(false); }}>
                {t('nav_properties')}
              </Button>
              <Button fullWidth onClick={() => { navigate('/chantiers'); setMobileMenuOpen(false); }}>
                {t('nav_developments')}
              </Button>
              <Button fullWidth onClick={() => { navigate('/crm'); setMobileMenuOpen(false); }}>
                {t('nav_for_professionals')}
              </Button>
              <Divider />
              <Button fullWidth startIcon={<Login />} onClick={() => navigate('/login')}>
                {t('navLogin')}
              </Button>
              <Button fullWidth variant="contained" startIcon={<PersonAdd />} onClick={() => navigate('/login')}>
                {t('navSignUp')}
              </Button>
            </Stack>
          </Container>
        </Box>
      )}
    </>
  );
};

export default PublicHeader;
